<script lang="ts">
  import Modal from "$components/+blocks/Modal/Modal.svelte";
  import { community } from "$stores/community";
  import { auth } from "$stores/auth";
  import MultipleCheckboxesField, {
    type CheckboxOption,
  } from "$components/form/Fields/MultipleCheckboxesField.svelte";
  import LoadingAtom from "$components/+blocks/Loading/LoadingAtom.svelte";
  import slack from "$assets/images/logos/Slack.svg?url";
  import {
    EventEnum,
    type NotificationChannelPreference,
  } from "@outverse-video/outverse-api-client-lib";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import Badge from "$components/+blocks/Badge/Badge.svelte";
  import { canReceiveAdminNotifications } from "$stores/permissions";
  import { browser } from "$app/environment";
  import { env } from "$env/dynamic/public";
  import api from "api";

  const BASE_SLACK_URL = `https://slack.com/oauth/v2/authorize?client_id=${env.PUBLIC_SLACK_CLIENT_ID}&scope=chat:write&redirect_uri=${env.PUBLIC_SLACK_REDIRECT_URI}`;

  export let open: boolean;

  function handleChange(eventType: EventEnum) {
    return (changeEvent: { detail: string | string[] }) => {
      api.communitiesMembersNotificationPreferences2({
        communitySlug: $community.slug!,
        uuid: $auth.user!.uuid,
        patchedNotificationChannelPreference: {
          event: eventType,
          email: changeEvent.detail.includes("Email"),
          slack: changeEvent.detail.includes("Slack"),
        },
      });
    };
  }

  let hasSlackOauth = false;
  type ChannelType = "Email" | "Slack";
  let channelTypes: CheckboxOption[];
  $: channelTypes = [
    { value: "Email", disabled: false },
    { value: "Slack", disabled: !hasSlackOauth },
  ];

  let selectedNewMember: ChannelType[] = [];
  let selectedNewReport: ChannelType[] = [];

  let selectedNewThread: ChannelType[] = [];
  let selectedNewReply: ChannelType[] = [];
  let selectedNewDirectReply: ChannelType[] = [];
  let selectedNewMention: ChannelType[] = [];

  let slack_url: string;
  $: if (browser) {
    const state = {
      community: $community.slug,
      user: $auth.user?.uuid,
      start_page: `${window.location.origin}${window.location.pathname}?channel-preferences=true`,
    };

    slack_url = `${BASE_SLACK_URL}&state=${btoa(JSON.stringify(state))}`;
  }

  function getChannelTypes(channelPreference: NotificationChannelPreference) {
    let channelTypes: ChannelType[] = [];
    if (channelPreference.email) {
      channelTypes.push("Email");
    }

    if (channelPreference.slack) {
      channelTypes.push("Slack");
    }

    return channelTypes;
  }

  function refreshNotificationPreferences() {
    if (!$auth.user) return;
    const notificationPreferencesRequest = api.communitiesMembersNotificationPreferences({
      communitySlug: $community.slug!,
      uuid: $auth.user.uuid,
    });

    notificationPreferencesRequest.then((resp) => {
      hasSlackOauth = resp.data.hasSlackOauth;

      for (let pref of resp.data.channelPreferences) {
        const channelTypes = getChannelTypes(pref);
        switch (pref.event) {
          case EventEnum.CommunityMemberJoined:
            selectedNewMember = channelTypes;
            break;
          case EventEnum.ThreadReported:
            selectedNewReport = channelTypes;
            break;
          case EventEnum.ThreadCreated:
            selectedNewThread = channelTypes;
            break;
          case EventEnum.ReplyCreated:
            selectedNewReply = channelTypes;
            break;
          case EventEnum.DirectReplyCreated:
            selectedNewDirectReply = channelTypes;
            break;
          case EventEnum.MentionCreated:
            selectedNewMention = channelTypes;
            break;
        }
      }
    });
    return notificationPreferencesRequest;
  }

  let notificationPreferencesRequest: Promise<any> | undefined;

  $: {
    if (open) {
      notificationPreferencesRequest = refreshNotificationPreferences();
    }
  }

  function disableSlack() {
    api
      .communitiesMembersDisableSlack({
        communitySlug: $community.slug!,
        uuid: $auth.user!.uuid,
      })
      .then(() => {
        hasSlackOauth = false;
        notificationPreferencesRequest = refreshNotificationPreferences();
      });
  }
</script>

<Modal bind:open withCloseIcon maxWidth="max-w-[600px]">
  <div class="p-6 text-sm text-neutral-500 dark:text-neutral-300">
    <div class="flex items-center gap-2">
      <Avatar
        mainImage={$community.logoImage}
        size="size-9"
        radius="rounded-lg"
        backgroundColor={$community.primaryColor}
      />
      <h3 class="text-base font-medium text-neutral-600 dark:text-white">Notifications</h3>
    </div>

    <p class="my-3 text-xs font-medium text-neutral-500/70 dark:text-neutral-400/80">
      Configure which channels you wish to receive notifications to for {$community.name}
    </p>

    <!-- SLACK -->
    <div
      class="flex items-center justify-between rounded border border-neutral-400/30 px-1.5 py-1.5 text-xs font-medium dark:border-white/10 dark:bg-white/3"
    >
      <div class="flex items-center gap-2">
        <img src={slack} alt="" class="size-4" />
        <span>Slack notifications</span>

        {#if hasSlackOauth}
          <Badge
            fontSize="text-xxs"
            radius="rounded"
            verticalPadding="py-0.5"
            horizontalPadding="px-1.5"
            lineHeight="leading-none"
            textColor="text-neutral-500 dark:text-neutral-100"
            backgroundColor="bg-green-500/30"
          >
            Enabled
          </Badge>
        {/if}
      </div>
      <a
        on:click={() => {
          if (hasSlackOauth) {
            disableSlack();
          }
        }}
        href={!hasSlackOauth ? slack_url : undefined}
        class="cursor-pointer rounded border border-neutral-400/30 bg-transparent px-2 py-1 text-center text-neutral-500 hover:bg-neutral-400/20 dark:border-white/20 dark:bg-white/5 dark:text-neutral-100 dark:hover:bg-white/20"
      >
        {hasSlackOauth ? "Disable" : "Enable"}
      </a>
    </div>

    <Separator class="mt-2 mb-4" />

    <div
      class="ml-auto flex w-1/4 justify-around text-sm font-medium text-neutral-500 dark:text-neutral-200"
    >
      <p>Email</p>
      <span class="flex" class:opacity-30={!hasSlackOauth}>
        <p>Slack</p>
      </span>
    </div>

    {#await notificationPreferencesRequest}
      {#if $canReceiveAdminNotifications}
        <LoadingAtom size="col-span-2 h-4 w-24" />
        {#each Array(2) as _}
          <div class="my-4 flex justify-between">
            <LoadingAtom size="col-span-2 h-4 w-40" />
            <div class="flex basis-1/4 justify-around gap-2">
              <LoadingAtom size="col-span-2 size-4" />
              <LoadingAtom size="col-span-2 size-4" />
            </div>
          </div>
        {/each}
        <Separator class="mt-2 mb-4" />
      {/if}

      <LoadingAtom size="col-span-2 h-4 w-24 mt-5" />

      {#each Array(4) as _}
        <div class="my-4 flex justify-between">
          <LoadingAtom size="col-span-2 h-4 w-40" />
          <div class="flex basis-1/4 justify-around gap-2">
            <LoadingAtom size="col-span-2 size-5" />
            <LoadingAtom size="col-span-2 size-5" />
          </div>
        </div>
      {/each}
    {:then}
      {#if $canReceiveAdminNotifications}
        <h3 class="mb-2 text-base font-semibold text-neutral-700 dark:text-white">Admin</h3>
        <MultipleCheckboxesField
          on:change={handleChange(EventEnum.CommunityMemberJoined)}
          name="newMember"
          label="New community member"
          value={selectedNewMember}
          options={channelTypes}
        />
        <MultipleCheckboxesField
          on:change={handleChange(EventEnum.ThreadReported)}
          name="selectedNewReport"
          label="Thread/Reply reported"
          value={selectedNewReport}
          options={channelTypes}
        />

        <Separator class="mt-2 mb-4" />
      {/if}

      <h3 class="mb-2 text-base font-semibold text-neutral-700 dark:text-white">Forums</h3>
      <p class="mt-2 mb-4 text-xs font-medium text-neutral-500/70 dark:text-neutral-400/80">
        You’ll receive notifications for forums/threads you are subscribed to
      </p>

      <MultipleCheckboxesField
        on:change={handleChange(EventEnum.ThreadCreated)}
        name="newThread"
        label="New thread created"
        value={selectedNewThread}
        options={channelTypes}
      />
      <MultipleCheckboxesField
        on:change={handleChange(EventEnum.ReplyCreated)}
        name="newReply"
        label="New reply in subscribed thread"
        value={selectedNewReply}
        options={channelTypes}
      />
      <MultipleCheckboxesField
        on:change={handleChange(EventEnum.DirectReplyCreated)}
        name="newDirectReply"
        label="New direct reply"
        value={selectedNewDirectReply}
        options={channelTypes}
      />
      <MultipleCheckboxesField
        on:change={handleChange(EventEnum.MentionCreated)}
        name="newMention"
        label="New mention"
        value={selectedNewMention}
        options={channelTypes}
      />
    {/await}
  </div>
</Modal>
